import {
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getAccountNameListAction } from "../../../../../store/reducers/leadSlice";
import ManageReffer from "../../../settings/manageReffer/ManageReffer";
import { setAddReferrerResponse } from "../../../../../store/reducers/referrerSlice";
import { setNotificationAction } from "../../../../../store/reducers/mmymSlice";
// import LeadCustomerList from "./CustomerLead/LeadCustomerList";
import { getAllSaleAction } from "../../../../../store/reducers/saleSlice";
import { getAllSubUserAction } from "../../../../../store/reducers/subUserSlice";
import { storeCustomerAllFields } from "./storeFieldUtils";
import FormField from "../../../../../components/common/FormField";
import { setNewStorageLocal } from "../../../../../store/reducers/storageSlice";
import LeadCustomerList from "../../../lead/CustomerLead/LeadCustomerList";

interface ManageCustomDetailsProps {
  checkValidator?: any;
  resetValidator?: any;
  error?: any;
  formData?: any;
  setFormData?: any;
  selectLead?: any;
  setCountryCode?: any;
  countryCode?: any;
  setAlternateCountryCode?: any;
  alternateCountryCode?: any;
}

const CustomerDetailsForm: FC<ManageCustomDetailsProps> = ({
  checkValidator,
  resetValidator,
  error,
  formData,
  setFormData,
  selectLead,
  setCountryCode,
  countryCode,
  setAlternateCountryCode,
  alternateCountryCode,
}) => {
  const dispatch = useDispatch();

  const isSmallScreen = useMediaQuery("(max-width:1024px)");
  const { referrers, accountNamesList } = useSelector(
    (state: any) => state.lead,
  );

  const { newStorageLocal } = useSelector((state: any) => state.storage);
  const { isAddReferrerSuccess } = useSelector((state: any) => state.referrer);
  const { sales } = useSelector((state: any) => state.sale);
  const { users } = useSelector((state: any) => state.subUser);
  const [showAccount, setShowAccount] = useState(false);
  const { userProfile } = useSelector((state: any) => state?.user);
  const isProfessionalSection =
    userProfile?.features?.includes("Professional Lead") || false; // Safe fallback

  const [showCustomerList, setShowCustomerList] = useState(false);

  const safeCustomer = newStorageLocal?.customer || {};

  const updatedFields = useMemo(() => {
    // Format accountNamesList for suggestions: label = prefix-accountName, value = accountName, prefix = prefix
    const formattedAccountNameSuggestions = (accountNamesList || []).map(
      (item: any) => ({
        label: item.prefix
          ? `${item.prefix}-${item.accountName}`
          : item.accountName,
        value: item.accountName,
        prefix: item.prefix || "",
      }),
    );
    return storeCustomerAllFields.map((field) => {
      if (field.id === "referrerId") {
        return { ...field, options: referrers, type: "select" };
      } else if (field.id === "accountName") {
        return {
          ...field,
          type: "suggestionText",
          suggestions: formattedAccountNameSuggestions,
        };
      } else if (field?.id === "leadTypeId") {
        return { ...field, options: sales?.lead_types };
      } else if (field?.id === "salesPersonId") {
        const salesUsers = (users || [])
          .filter((user: any) => user)
          .map((user: any) => ({
            label: user?.name || "",
            id: user?.staff_id ?? user?.tenant_id,
          }));

        return {
          ...field,
          options: [{ label: "Anyone", id: "" }, ...salesUsers],
        };
      }
      return field;
    });
  }, [referrers, sales, users, accountNamesList]);

  useEffect(() => {
    if (
      isProfessionalSection &&
      newStorageLocal &&
      newStorageLocal?.salesPersonId === undefined
    ) {
      dispatch(
        setNewStorageLocal({
          ...newStorageLocal,
          salesPersonId: userProfile?.id ?? null,
          salesPersonName: userProfile?.id ? userProfile?.name : "Anyone",
        }),
      );
    }
  }, [userProfile, isProfessionalSection, dispatch, newStorageLocal]);

  useEffect(() => {
    if (
      newStorageLocal &&
      newStorageLocal?.salesPersonId === null &&
      newStorageLocal?.salesPersonName !== "Anyone"
    ) {
      dispatch(
        setNewStorageLocal({
          ...newStorageLocal,
          salesPersonName: "Anyone",
        }),
      );
    }
  }, [newStorageLocal, dispatch]);

  useEffect(() => {
    if (isProfessionalSection) {
      dispatch(getAllSaleAction());
      dispatch(getAccountNameListAction());
      dispatch(getAllSubUserAction({ status: "ACTIVE" }));
    }
  }, [isProfessionalSection, dispatch]);

  const onChangeAction = (event: any, option: any = null) => {
    const { id, value } = event.target;
    // Determine if the field belongs to the customer object
    const isCustomerField = [
      "name",
      "phone",
      "alternatePhone",
      "email",
      "accountName",
      "prefix",
    ].includes(id);

    let updatedFields: any = {};

    if (id === "movingOnDate" && Array.isArray(value)) {
      updatedFields = {
        movingOnDate: value[0], // First date
        movingOnEndDate: value[1], // Second date
      };
    } else if (id === "salesPersonId") {
      const selectedSalesUser = users.find(
        (user: any) =>
          String(user.staff_id ?? user?.tenant_id) === String(value),
      );

      updatedFields = {
        salesPersonId:
          value === ""
            ? null
            : (selectedSalesUser?.staff_id ??
              selectedSalesUser?.tenant_id ??
              value),
        sales_person_name:
          value === "" ? "Anyone" : selectedSalesUser?.name || "Anyone",
      };
    } else if (id === "accountName" && option && option.prefix !== undefined) {
      // If account_name selected from suggestions, store only string for account_name and set prefix
      updatedFields = {
        account_name: option.value,
        prefix: option.prefix,
      };
    } else if (
      id === "accountName" &&
      value &&
      typeof value === "object" &&
      value.value
    ) {
      // Defensive: if value is object, extract string
      updatedFields = {
        account_name: value.value,
        prefix: value.prefix || "",
      };
    } else {
      updatedFields = {
        [id]: value,
      };
    }

    const updatedNewStorageLocal = {
      ...newStorageLocal,
      ...(isCustomerField
        ? {
            customer: {
              ...(newStorageLocal?.customer || {}),
              ...updatedFields,
            },
          }
        : updatedFields),
    };

    dispatch(setNewStorageLocal(updatedNewStorageLocal));

    setFormData((prevState: any) => ({
      ...prevState,
      ...(isCustomerField
        ? {
            customer: {
              ...prevState.customer,
              ...updatedFields,
            },
          }
        : updatedFields),
    }));

    resetValidator(id);
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  const [showReferrerModel, setShowReferrerModel] = useState(false);
  const addReferrerHandler = () => {
    setShowReferrerModel(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };
  const handleClose = () => {
    setShowReferrerModel(false);
  };

  useEffect(() => {
    if (isAddReferrerSuccess) {
      setShowReferrerModel(false);
      dispatch(setAddReferrerResponse(false));
    }
  }, [isAddReferrerSuccess, dispatch]);

  useEffect(() => {
    if (
      newStorageLocal?.customer?.accountName ||
      newStorageLocal?.customer?.prefix
    ) {
      setShowAccount(true);
    } else {
      setShowAccount(false);
    }
  }, [newStorageLocal]);

  const handleRadioChange = (e: any) => {
    const value = e.target.value === "yes";
    setShowAccount(value);
  };

  const handleSearchCustomer = () => {
    setShowCustomerList(true);
  };

  return (
    <>
      {showReferrerModel && (
        <ManageReffer
          selectUser={""}
          open={showReferrerModel}
          onHide={handleClose}
          title={"Add Referrer"}
          isSmallScreen={true}
        />
      )}

      {showCustomerList && (
        <LeadCustomerList
          setCountryCode={setCountryCode}
          open={showCustomerList}
          setAlertModelShow={setShowCustomerList}
          title={"Customer List"}
          onHide={() => {
            setShowCustomerList(false);
          }}
        />
      )}
      <Grid
        container
        direction="row"
        spacing={0.5}
        rowGap={0}
        rowSpacing={0}
        sx={{
          background: "#E5F4F1",
          padding: "10px !important",
          marginTop: "10px !important",
          borderRadius: "5px",
          border: "1px solid #CFCFCF",
          overflow: "hidden",
        }}
      >
        <Grid item xs={12} sm={12} style={{ padding: 0 }}>
          <Typography
            align="left"
            sx={{
              textAlign: "left",
              padding: "0px",
              fontWeight: 600,
              color: "#333333",
            }}
            variant="subtitle1"
          >
            Customer Details
          </Typography>
        </Grid>
        {updatedFields?.map((field, i) => {
          // Hide Professional fields if feature not enabled
          if (field.professionalOnly && !isProfessionalSection) {
            return null;
          }
          // If field is NOT professionalOnly but section IS Professional → hide
          if (field?.id === "moveType" && isProfessionalSection) {
            return null;
          }
          // Hide account fields when radio = No
          if (
            !showAccount &&
            (field.id === "accountName" || field.id === "prefix")
          ) {
            return null;
          }
          // Special block for radio type
          if (field.type === "radio") {
            return (
              <Grid
                key={field.id}
                item
                xs={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "18px !important",
                  paddingTop: "6px !important",
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: "14px", mr: 2 }}>
                  {field.label}
                </Typography>

                <RadioGroup
                  row
                  value={showAccount ? "yes" : "no"}
                  onChange={handleRadioChange}
                  sx={{ fontSize: "14px" }}
                  // aria-disabled={newFinancialState?.id ? true : false}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="Yes"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px", // <-- label text size
                      },
                    }}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="No"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px", // <-- label text size
                      },
                    }}
                  />
                </RadioGroup>
              </Grid>
            );
          }
          return (
            <>
              <FormField
                key={field.id}
                onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                  const pastedText = e.clipboardData.getData("text");

                  if (field.id === "phone") {
                    // Allow only digits
                    if (!/^\d+$/.test(pastedText)) {
                      e.preventDefault();
                    }
                    return;
                  }

                  if (field.id === "name" || field.id === "email") {
                    return;
                  }

                  e.preventDefault();
                }}
                onKeyDown={(event: any) => {
                  if (
                    [8, 9, 37, 38, 39, 40, 18, 17].includes(event.keyCode) ||
                    event.ctrlKey
                  )
                    return event;
                  if (
                    field?.patternAllowed &&
                    !field?.patternAllowed.test(event.key)
                  ) {
                    event.preventDefault();
                  } else if (
                    field?.patternAllowed &&
                    !field?.patternAllowed.test(event.target.value + event.key)
                  ) {
                    event.preventDefault();
                  }
                }}
                {...{
                  ...field,
                  value:
                    safeCustomer[field.id] ?? newStorageLocal?.[field.id] ?? "",
                  inSingleGridForm: true,
                  minDate: field.id === "movingOnDate" ? new Date() : undefined,
                  variant: "outlined",
                  onCountryCodeChange:
                    field.type === "contact"
                      ? (code: string) => {
                          if (field.id === "alternatePhone") {
                            setAlternateCountryCode?.(code);
                          } else {
                            setCountryCode?.(code);
                          }
                        }
                      : undefined,
                  ...{
                    ...(field?.type === "contact" && {
                      countryCode:
                        field.id === "alternatePhone"
                          ? alternateCountryCode ||
                            newStorageLocal?.customer?.alternateCountryCode ||
                            ""
                          : countryCode ||
                            newStorageLocal?.customer?.countryCode ||
                            "",
                    }),
                  },
                  onChange: onChangeAction,
                  onBlur: onBlurAction,
                  isNonMarginTop: true,
                  GridFieldStyle: {
                    paddingLeft:
                      field.type === "daterangebothvalue" ||
                      field.type === "date"
                        ? "8px !important"
                        : "auto",
                  },
                  InputProps: {
                    ...(field?.inputProps && field?.inputProps),
                    ...(selectLead?.generated_id && {
                      disabled: field?.editReadOnly,
                    }),
                    sx:
                      field.type === "text" || field.type === "password"
                        ? { height: "36px" }
                        : { padding: "10px 4px", height: "36px" },
                  },
                  error: error && error[field.id],
                }}
              />
              {field?.id === "referrerId" && (
                <Grid
                  item
                  sm={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingLeft: "0px !important",
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addReferrerHandler}
                    sx={{
                      fontSize: "10px",
                      marginLeft: "6px",
                      height: "30px",
                      marginTop: "18px",
                      width: { sm: isSmallScreen ? "min-content" : "auto" },
                    }}
                  >
                    {isSmallScreen ? "Add" : "Add more"}
                  </Button>
                </Grid>
              )}
              {field?.id === "advertId" && (
                <Grid
                  item
                  sm={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingLeft: "0px !important",
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSearchCustomer}
                    sx={{
                      fontSize: "10px",
                      marginLeft: "6px",
                      height: "30px",
                      marginTop: "18px",
                      width: { sm: isSmallScreen ? "min-content" : "auto" },
                    }}
                  >
                    {isSmallScreen ? "🔍" : "Search"}
                  </Button>
                </Grid>
              )}
            </>
          );
        })}
      </Grid>
    </>
  );
};

export default CustomerDetailsForm;
